import { SubjectAreaNames } from '../../../interfaces/EOAInterface';
import { TranscriptPrintData, TranscriptPrintError } from '../../../interfaces/TranscriptsInterface';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { ACADEMIC_PERIODS_LABELS, ACADEMIC_PERIODS_NAMES } from '../../../constants';
import TranscriptPrintViewCell, {
  TranscriptPrintViewCellPreview,
  TranscriptPrintViewCellFull,
} from './TranscriptPrintViewCell';
import { trimTo2Decimals } from '../../../utils';

import './TranscriptPrintView.css';

interface TranscriptPrintViewProps {
  transcript: TranscriptPrintData | TranscriptPrintError;
  subjectAreas: SubjectAreaNames[];
}

const getCTECourses = (term: any) => {
  const cteCoursesMap = new Map();

  term.subject_areas.forEach((subjectArea: any) => {
    subjectArea.courses.forEach((course: any) => {
      if (course.is_cte) {
        const key = `${course.code}-${course.name}`;
        if (!cteCoursesMap.has(key)) {
          cteCoursesMap.set(key, course);
        }
      }
    });
  });

  return Array.from(cteCoursesMap.values());
};

const getTotalCTECredits = (data: any) => {
  const cteCoursesMap = new Map();

  data.academic_periods.forEach((academicPeriod: any) => {
    academicPeriod.terms.forEach((term: any) => {
      const cteCourses = getCTECourses(term);
      cteCourses.forEach((course: any) => {
        const key = `${course.code}-${course.name}`;
        if (!cteCoursesMap.has(key)) {
          const earnedCredits = course.credits_earned > 0 ? course.credits_earned : 0;
          cteCoursesMap.set(key, earnedCredits);
        }
      });
    });
  });

  return Array.from(cteCoursesMap.values()).reduce((total, credits) => total + credits, 0);
};

const getSubjectAreaEarnedCredits = (data: any, subjectAreaName: string) => {
  let earnedCredits = 0;
  data.academic_periods.forEach((academicPeriod: any) => {
    academicPeriod.terms.forEach((term: any) => {
      const subjectAreaData = term.subject_areas.find((sa: any) => sa.name === subjectAreaName);
      if (subjectAreaData) {
        subjectAreaData.courses.forEach((course: any) => {
          const courseEarnedCredits = course.credits_earned > 0 ? course.credits_earned : 0;
          earnedCredits += courseEarnedCredits;
        });
      }
    });
  });
  return earnedCredits;
};

const renderCTECoursesCell = (term: any) => {
  const cteCourses = getCTECourses(term);

  if (cteCourses.length === 0) return <TableCell className="border-left" />;

  return (
    <TableCell className="border-left data-cell">
      {cteCourses.length > 1 ? (
        <TranscriptPrintViewCellPreview data={cteCourses} />
      ) : (
        <TranscriptPrintViewCellFull data={cteCourses[0]} />
      )}
    </TableCell>
  );
};

function TranscriptPrintView({ transcript, subjectAreas }: TranscriptPrintViewProps) {
  if (transcript.type === 'error')
    return <p className="page">Student {transcript.studentNumber}'s data is unavailable!</p>;

  let data = transcript.data;

  const totalCTECredits = getTotalCTECredits(data);

  return (
    <div className="page">
      <div className="print-view-header">
        <p className="student-number">Student {data.school_student_number}</p>
        <div className="student-info">
          <p className="no-margin">
            <span className="bolded">GPA</span> {trimTo2Decimals(data.gpa)}
          </p>
          <p className="no-margin">
            <span className="bolded">Credits</span> {data.credits_earned.toString().toLowerCase()}
          </p>
          <p className="no-margin">
            <span className="bolded">Gender</span> {data.gender.toLowerCase()}
          </p>
          <p className="no-margin">
            <span className="bolded">Race/ethnicity</span> {data.race.toLowerCase()}
          </p>
          <p className="no-margin">
            <span className="bolded">Disability status</span> {data.student_with_disabilities.toLowerCase()}
          </p>
          <p className="no-margin">
            <span className="bolded">English language learner status</span> {data.english_learner.toLowerCase()}
          </p>
          <p className="no-margin">
            <span className="bolded">Low Income</span> {data.low_income == null ? 'N/A' : data.low_income.toLowerCase()}
          </p>
        </div>
      </div>
      <TableContainer>
        <Table className="print-view-subject-area-table">
          <TableHead>
            <TableRow>
              <TableCell className="header-cell grade-cell"></TableCell>
              <TableCell className="header-cell semester-cell"></TableCell>
              {subjectAreas.map((subjectArea) => {
                const subjectAreaCreditsCount = getSubjectAreaEarnedCredits(data, subjectArea);
                return (
                  <TableCell
                    key={subjectArea}
                    className="header-cell dynamic"
                    sx={{ width: `${88 / (subjectAreas.length + 1)}%`, minWidth: 50 }}
                  >
                    <div className="header-cell-content">
                      <p className="no-margin subject-area-name">{subjectArea}</p>
                      <p className="no-margin regular subject-area-credits">{subjectAreaCreditsCount} credits</p>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell
                className="header-cell dynamic"
                sx={{ width: `${88 / (subjectAreas.length + 1)}%`, minWidth: 50 }}
              >
                <div className="header-cell-content">
                  <p className="no-margin subject-area-name">CTE</p>
                  <p className="no-margin regular subject-area-credits">{totalCTECredits} credits</p>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.academic_periods.map((academicPeriod, academicPeriodIndex) => {
              let rowCount = academicPeriod.terms.length;
              let academicPeriodCreditsCount = academicPeriod.total_credits_earned ?? 0;
              let rows = [];

              for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
                if (rowIndex === 0) {
                  rows.push(
                    <TableRow
                      key={`${academicPeriodIndex}-${rowIndex}`}
                      className={`print-view-table-row ${academicPeriodIndex % 2 !== 0 ? 'highlighted' : ''}`}
                    >
                      <TableCell rowSpan={rowCount}>
                        <span className="bolded">
                          {ACADEMIC_PERIODS_LABELS[academicPeriod.period as keyof typeof ACADEMIC_PERIODS_NAMES]}
                        </span>
                        <p className="no-margin">{academicPeriodCreditsCount} credits</p>
                      </TableCell>
                      <TableCell className="border-left">{academicPeriod.terms[rowIndex].name}</TableCell>
                      {subjectAreas.map((subjectArea) => (
                        <TranscriptPrintViewCell
                          key={subjectArea}
                          subjectArea={subjectArea}
                          term={academicPeriod.terms[rowIndex]}
                        />
                      ))}
                      {renderCTECoursesCell(academicPeriod.terms[rowIndex])}
                    </TableRow>
                  );
                } else {
                  rows.push(
                    <TableRow
                      key={`${academicPeriodIndex}-${rowIndex}`}
                      className={`print-view-table-row ${academicPeriodIndex % 2 !== 0 ? 'highlighted' : ''}`}
                    >
                      <TableCell className="border-left">{academicPeriod.terms[rowIndex].name}</TableCell>
                      {subjectAreas.map((subjectArea) => (
                        <TranscriptPrintViewCell
                          key={subjectArea}
                          subjectArea={subjectArea}
                          term={academicPeriod.terms[rowIndex]}
                        />
                      ))}
                      {renderCTECoursesCell(academicPeriod.terms[rowIndex])}
                    </TableRow>
                  );
                }
              }

              return rows;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default TranscriptPrintView;
